import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  fetchDevices, fetchMe, addDevice, deleteDevice, deviceLink, resetHwid, buyExtraDevice,
  fetchDeviceQuote, ApiError, type Device, type DeviceLink,
} from '@/lib/api'
import { useLocation } from 'wouter'
import { motion } from 'motion/react'
import { AppHeader } from '@/components/AppHeader'
import { Card } from '@/components/Surface'
import { Sheet, SheetButton } from '@/components/Sheet'
import { Segment } from '@/components/Form'
import { SubStatus } from '@/components/SubStatus'
import { DeviceQuote } from '@/components/DeviceQuote'
import { haptic, openExternal, openHapp, openKoala, tg } from '@/lib/tg'

function errText(e: unknown): string {
  const reason = e instanceof ApiError ? (e.body as { error?: string } | undefined)?.error : undefined
  switch (reason) {
    case 'device_limit': return 'Достигнут лимит устройств для вашего тарифа.'
    case 'subscription_inactive': return 'Подписка неактивна — оформите или продлите тариф.'
    case 'rate_limited': return 'Слишком часто. Подождите немного.'
    case 'cooldown': return 'Сброс доступен раз в 30 минут. Подожди немного.'
    default: return 'Не удалось выполнить. Попробуйте позже.'
  }
}
function notify(m: string) {
  const wa = tg(); if (wa?.showPopup) wa.showPopup({ message: m }); else alert(m)
}
// INCY install links (Happ → INCY migration). Const names kept to minimise the diff;
// the VALUES are the live INCY App Store / Google Play pages.
const HAPP_IOS = 'https://apps.apple.com/app/id6756943388'
const HAPP_ANDROID = 'https://play.google.com/store/apps/details?id=llc.itdev.incy'
const KOALA_DOWNLOAD = 'https://github.com/coolcoala/koala-clash/releases/latest'

type Platform = 'ios' | 'android' | 'windows' | 'macos'
const PLATFORMS = [
  { value: 'ios' as const, label: 'iOS' },
  { value: 'android' as const, label: 'Android' },
  { value: 'windows' as const, label: 'Windows' },
  { value: 'macos' as const, label: 'Mac' },
]
const defLabel = (p: Platform) =>
  p === 'ios' ? 'iPhone' : p === 'android' ? 'Android' : p === 'windows' ? 'Windows ПК' : 'Mac'
// Koala Clash (mihomo core) deep link. The sub endpoint returns a Clash config
// when fetched with a koala/clash UA, so the same sub_link drives Koala Clash.
const clashDeepLink = (sub: string) =>
  `clash://install-config?url=${encodeURIComponent(sub)}&name=${encodeURIComponent('Цикада')}`

function formatLastSeen(ts?: string | null) {
  if (!ts) return 'не в сети'
  const diff = Date.now() - new Date(ts).getTime()
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'только что'
  if (min < 60) return `${min} мин назад`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} ч назад`
  return new Date(ts).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

type Tone = 'online' | 'offline' | 'none' | 'shared'
function binding(d: Device): { text: string; tone: Tone } {
  const b = d.bound
  if (b && b.count > 1) return { text: `${b.count} устройства на ключе`, tone: 'shared' }
  if (b && b.model) {
    const dev = [b.model, b.os].filter(Boolean).join(' · ')
    return d.is_online ? { text: `в сети · ${dev}`, tone: 'online' }
                       : { text: `${dev} · ${formatLastSeen(d.last_seen)}`, tone: 'offline' }
  }
  return d.is_online ? { text: 'в сети', tone: 'online' } : { text: 'не подключено', tone: 'none' }
}
function iconKind(d: Device): 'phone' | 'desktop' | 'router' {
  const os = (d.bound?.os || '').toLowerCase()
  if (os.includes('ios') || os.includes('android')) return 'phone'
  if (os.includes('mac') || os.includes('win') || os.includes('linux')) return 'desktop'
  if (d.platform === 'ios' || d.platform === 'android') return 'phone'
  if (d.platform === 'linux') return 'router'
  return 'desktop'
}

export function Devices() {
  const qc = useQueryClient()
  const [, setLocation] = useLocation()
  const { data: devices } = useQuery({ queryKey: ['devices'], queryFn: fetchDevices })
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe })
  const limit = me?.subscription?.device_limit ?? 1
  const count = devices?.length ?? 0
  const atLimit = count >= limit
  const primaryId = devices?.[0]?.id

  const [addOpen, setAddOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [platform, setPlatform] = useState<Platform>('ios')
  const [newLink, setNewLink] = useState<DeviceLink | null>(null)

  const [active, setActive] = useState<Device | null>(null)
  const [activeLink, setActiveLink] = useState<DeviceLink | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [extraOpen, setExtraOpen] = useState(false)

  // prorated price for ONE extra device until the subscription ends — from backend quote
  const { data: dq } = useQuery({
    queryKey: ['deviceQuote'],
    queryFn: () => fetchDeviceQuote(1),
    enabled: extraOpen,
  })

  const add = useMutation({
    mutationFn: () => addDevice(label.trim() || defLabel(platform), platform),
    onSuccess: dev => { qc.invalidateQueries({ queryKey: ['devices'] }); setNewLink({ happ_link: dev.happ_link, sub_link: dev.sub_link }) },
    onError: e => { setAddOpen(false); notify(errText(e)) },
  })
  const buyExtra = useMutation({
    mutationFn: () => buyExtraDevice(),
    onSuccess: res => {
      setExtraOpen(false)
      if (res.kind === 'url' && res.url) openExternal(res.url)
      else if (res.kind === 'done') { qc.invalidateQueries({ queryKey: ['me'] }); notify('Слот добавлен! Теперь добавь устройство.') }
      else notify('Оплата временно недоступна. Попробуй позже.')
    },
    onError: e => { setExtraOpen(false); notify(errText(e)) },
  })
  const del = useMutation({
    mutationFn: (id: number) => deleteDevice(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['devices'] }); closeDevice() },
    onError: e => { closeDevice(); notify(errText(e)) },
  })
  const reset = useMutation({
    mutationFn: () => resetHwid(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices'] }); setResetOpen(false)
      notify('Привязка сброшена. Открой ключ на новом телефоне — он привяжется к нему.')
    },
    onError: e => { setResetOpen(false); notify(errText(e)) },
  })

  const openAdd = () => { haptic('medium'); setLabel(''); setPlatform('ios'); setNewLink(null); setAddOpen(true) }
  const openDevice = async (d: Device) => {
    haptic('light'); setActive(d); setActiveLink(null); setConfirmRevoke(false)
    try { setActiveLink(await deviceLink(d.id)) } catch { setActiveLink(null) }
  }
  const closeDevice = () => { setActive(null); setActiveLink(null); setConfirmRevoke(false) }
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); notify('Ссылка скопирована') } catch { openExternal(text) }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      className="flex-1 flex flex-col min-h-0 bg-[var(--color-bg)]"
    >
      <AppHeader />
      <header className="px-5 pt-1 pb-3">
        <div className="rule w-10" />
        <h1 className="display text-[34px] leading-[0.88] mt-2.5">устройства</h1>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto pb-8">
      <div className="px-5">
        <div className="mb-4"><SubStatus /></div>
        <Card className="overflow-hidden">
          {(devices ?? []).map((d, i, arr) => {
            const b = binding(d)
            return (
              <button key={d.id} onClick={() => openDevice(d)}
                className="tap w-full text-left flex items-center gap-3 px-4 py-3.5"
                style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--color-line)' : 'none' }}>
                <DeviceIcon kind={iconKind(d)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-semibold truncate">{d.label}</span>
                    {i === 0 && (
                      <span className="shrink-0 num text-[8px] tracking-wider uppercase px-1.5 py-0.5 rounded-[4px]"
                        style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent)' }}>осн.</span>
                    )}
                  </div>
                  <div className="text-[11.5px] mt-0.5 flex items-center gap-1.5 truncate"
                    style={{ color: b.tone === 'shared' ? 'var(--color-accent)' : 'var(--color-muted)' }}>
                    {b.tone === 'shared'
                      ? <span>⚠️ {b.text}</span>
                      : <>
                          <span className="block w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ background: b.tone === 'online' ? 'var(--color-green)' : 'var(--color-dim)' }} />
                          <span className="truncate">{b.text}</span>
                        </>}
                  </div>
                </div>
                <span className="text-[var(--color-gold)] text-[18px] leading-none shrink-0">⋯</span>
              </button>
            )
          })}
          {count === 0 && <div className="px-4 py-6 text-center text-[12px] text-[var(--color-muted)]">пока нет устройств</div>}

          {atLimit ? (
            <button onClick={() => { haptic('medium'); setExtraOpen(true) }}
              className="tap w-full px-4 py-3.5 num text-[11px] font-bold tracking-[0.16em] uppercase text-center"
              style={{ borderTop: '1px solid var(--color-line)', color: 'var(--color-accent)' }}>
              + докупить устройство
            </button>
          ) : (
            <button onClick={openAdd}
              className="tap w-full px-4 py-3.5 num text-[11px] font-bold tracking-[0.16em] uppercase text-center"
              style={{ borderTop: '1px solid var(--color-line)', color: 'var(--color-accent)' }}>
              + добавить устройство
            </button>
          )}
        </Card>

        <p className="text-[11.5px] text-[var(--color-muted)] mt-3 leading-relaxed px-1">
          Каждое устройство — отдельный ключ. Открой ссылку на нужном телефоне — ключ привяжется к нему.
          {count >= 1 && <> Доп. устройство — <b className="text-[var(--color-ink)]">3.33 ₽/день</b> до конца подписки.</>}
        </p>
      </div>

      {/* Add device */}
      <Sheet open={addOpen} onClose={() => setAddOpen(false)} title={newLink ? 'устройство готово' : 'новое устройство'}>
        {!newLink ? (
          <>
            <div className="text-[12px] text-[var(--color-muted)] mb-2">платформа</div>
            <Segment value={platform} onChange={setPlatform} options={PLATFORMS} />
            <Card className="p-1 mt-3 mb-4">
              <input value={label} onChange={e => setLabel(e.target.value)} placeholder={defLabel(platform)}
                className="w-full bg-transparent px-4 py-3 text-[15px] outline-none" />
            </Card>
            <SheetButton tone="primary" onClick={() => add.mutate()} disabled={add.isPending}>
              {add.isPending ? 'создаём…' : 'создать профиль'}
            </SheetButton>
          </>
        ) : platform === 'windows' ? (
          <>
            <p className="text-[13.5px] text-[var(--color-muted)] mb-4 leading-relaxed">
              Профиль создан. Для Windows используем <b className="text-[var(--color-ink)]">Koala Clash</b>.
              Установи приложение и нажми кнопку — профиль добавится сам.
            </p>
            <SheetButton tone="primary" onClick={() => openKoala(clashDeepLink(newLink.sub_link), newLink.sub_link)}>открыть в Koala Clash</SheetButton>
            <div className="text-center text-[12px] text-[var(--color-muted)] mt-1 mb-1">
              нет приложения?{' '}
              <button onClick={() => openExternal(KOALA_DOWNLOAD)} className="text-[var(--color-gold-bright)] underline underline-offset-2">скачать&nbsp;Koala&nbsp;Clash</button>
            </div>
            <SheetButton onClick={() => copy(newLink.sub_link)}>скопировать ссылку</SheetButton>
            <p className="text-[11.5px] text-[var(--color-muted)] text-center px-2 pt-1 leading-relaxed">
              Если кнопка не открыла приложение — скопируй ссылку, затем в Koala Clash: «Профили» → импорт из буфера обмена.
            </p>
          </>
        ) : (
          <>
            <p className="text-[13.5px] text-[var(--color-muted)] mb-4 leading-relaxed">
              Профиль создан. Установи <b className="text-[var(--color-ink)]">INCY</b> и нажми кнопку — VPN добавится сам.
              Ключ привяжется именно к этому устройству.
            </p>
            <SheetButton tone="primary" onClick={() => openHapp(newLink.happ_link, newLink.sub_link)}>открыть в INCY</SheetButton>
            <div className="text-center text-[12px] text-[var(--color-muted)] mt-1 mb-1">
              нет приложения?{' '}
              <button onClick={() => openExternal(HAPP_IOS)} className="text-[var(--color-gold-bright)] underline underline-offset-2">App&nbsp;Store</button>
              {' · '}
              <button onClick={() => openExternal(HAPP_ANDROID)} className="text-[var(--color-gold-bright)] underline underline-offset-2">Google&nbsp;Play</button>
            </div>
            <SheetButton onClick={() => copy(newLink.sub_link)}>скопировать ссылку</SheetButton>
          </>
        )}
      </Sheet>

      {/* Buy one extra device slot — prorated to the days left in the subscription */}
      <Sheet open={extraOpen} onClose={() => setExtraOpen(false)} title="дополнительное устройство">
        <div className="text-[13.5px] text-[var(--color-muted)] mb-4 leading-relaxed">
          Сейчас <b className="text-[var(--color-ink)]">{count} из {limit}</b>. Добавим ещё один слот
          до конца текущей подписки.
        </div>
        <div className="mb-4">
          {dq
            ? <DeviceQuote mode="addon" dayRate={dq.day_rate} days={dq.days_left} cost={dq.cost} />
            : <Card className="p-4"><div className="text-[12.5px] text-[var(--color-muted)]">считаем цену…</div></Card>}
        </div>
        <SheetButton tone="primary" disabled={buyExtra.isPending || !dq?.cost} onClick={() => buyExtra.mutate()}>
          {buyExtra.isPending ? 'создаём оплату…' : `оплатить ${dq?.cost ?? ''} ₽ · СБП`}
        </SheetButton>
        <SheetButton onClick={() => { setExtraOpen(false); setLocation('/plans') }}>или сменить тариф →</SheetButton>
        <p className="text-[11.5px] text-[var(--color-muted)] text-center px-2 pt-1 leading-relaxed">
          Слот действует до конца подписки. При продлении выбери нужное число устройств сразу — выйдет дешевле.
        </p>
      </Sheet>

      {/* Device actions */}
      <Sheet open={!!active} onClose={closeDevice} title={active?.label}>
        {active && (confirmRevoke ? (
          <>
            <p className="text-[14px] font-semibold mb-2">Отозвать ключ «{active.label}»?</p>
            <ul className="text-[13px] text-[var(--color-muted)] leading-relaxed mb-4 space-y-1.5">
              <li>• Это устройство <b className="text-[var(--color-ink)]">навсегда потеряет доступ</b> к VPN.</li>
              <li>• Ключ удалится, восстановить нельзя.</li>
              <li>• Освободится слот устройства в твоём тарифе.</li>
            </ul>
            <SheetButton tone="danger" disabled={del.isPending} onClick={() => del.mutate(active.id)}>
              {del.isPending ? 'отзываем…' : 'Да, отозвать ключ'}
            </SheetButton>
            <SheetButton onClick={() => setConfirmRevoke(false)}>Отмена</SheetButton>
          </>
        ) : (
          <>
            <div className="text-[12.5px] text-[var(--color-muted)] mb-3 leading-relaxed">
              {active.bound && active.bound.count > 1
                ? <span style={{ color: 'var(--color-accent)' }}>⚠️ Этот ключ используется на {active.bound.count} устройствах.</span>
                : active.bound?.model
                  ? <>Привязан к: <b className="text-[var(--color-ink)]">{[active.bound.model, active.bound.os].filter(Boolean).join(' · ')}</b>{active.is_online ? ' · в сети' : ` · ${formatLastSeen(active.last_seen)}`}</>
                  : 'Ещё не подключено ни одно устройство.'}
            </div>
            {activeLink?.sub_link ? (
              active.platform === 'windows' ? (
                <>
                  <SheetButton tone="primary" onClick={() => openKoala(clashDeepLink(activeLink.sub_link), activeLink.sub_link)}>открыть в Koala Clash</SheetButton>
                  <div className="text-center text-[12px] text-[var(--color-muted)] mt-1 mb-1">
                    нет приложения?{' '}
                    <button onClick={() => openExternal(KOALA_DOWNLOAD)} className="text-[var(--color-gold-bright)] underline underline-offset-2">скачать&nbsp;Koala&nbsp;Clash</button>
                  </div>
                </>
              ) : (
                <SheetButton tone="primary" onClick={() => openHapp(activeLink.happ_link, activeLink.sub_link)}>открыть в INCY</SheetButton>
              )
            ) : (
              <SheetButton tone="primary" onClick={() => {}} disabled>загрузка ссылки…</SheetButton>
            )}
            <SheetButton onClick={() => { if (activeLink) copy(activeLink.sub_link) }}>скопировать ссылку</SheetButton>
            {active.id === primaryId ? (
              <p className="text-[12px] text-[var(--color-muted)] text-center px-2 pt-1 leading-relaxed">
                Это основное устройство — ключ с главного экрана. Его нельзя отозвать.
              </p>
            ) : (
              <SheetButton tone="danger" onClick={() => { haptic('medium'); setConfirmRevoke(true) }}>отозвать ключ</SheetButton>
            )}
            <button onClick={() => { haptic('light'); closeDevice(); setResetOpen(true) }}
              className="tap w-full mt-1 text-[12px] text-[var(--color-muted)] text-center py-2">
              📱 Сменил телефон? <span className="text-[var(--color-accent)]">Сбросить привязку</span>
            </button>
          </>
        ))}
      </Sheet>

      {/* New phone — self-service HWID reset */}
      <Sheet open={resetOpen} onClose={() => setResetOpen(false)} title="новый телефон">
        <p className="text-[13.5px] text-[var(--color-muted)] mb-4 leading-relaxed">
          Сбросит привязку твоих ключей к старым устройствам. После этого
          открой ключ на <b className="text-[var(--color-ink)]">новом телефоне</b> — он привяжется к нему.
          <br /><br />
          <span style={{ color: 'var(--color-accent)' }}>Старое устройство потеряет доступ.</span>
        </p>
        <SheetButton tone="primary" disabled={reset.isPending} onClick={() => reset.mutate()}>
          {reset.isPending ? 'сбрасываем…' : 'Сбросить привязку'}
        </SheetButton>
        <SheetButton onClick={() => setResetOpen(false)}>Отмена</SheetButton>
      </Sheet>
      </div>
    </motion.div>
  )
}

function DeviceIcon({ kind }: { kind: 'phone' | 'desktop' | 'router' }) {
  const c = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  return (
    <div className="size-9 rounded-[5px] bg-[var(--color-bg-deep)] border border-[var(--color-line)] flex items-center justify-center text-[var(--color-gold)] shrink-0">
      {kind === 'phone' && <svg {...c}><rect x="6" y="2.5" width="12" height="19" rx="2.5" /><path d="M11 18.5h2" /></svg>}
      {kind === 'router' && <svg {...c}><rect x="3" y="13" width="18" height="7" rx="1.5" /><path d="M7 17h.01M11 17h2M12 13V8M9 8a3 3 0 016 0M6.5 6a6 6 0 0111 0" /></svg>}
      {kind === 'desktop' && <svg {...c}><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>}
    </div>
  )
}
